import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
  Search, MapPin, Briefcase, Award, X, Filter, RefreshCw, 
  Send, CheckCircle2, User, Building, PhoneCall, Mail, ChevronLeft, ChevronRight,
  ShieldCheck, Sparkles, MessageSquare, Globe, GraduationCap, Clock
} from 'lucide-react';
import PublicCoachHeader from './PublicCoachHeader';
import { toast } from '../lib/toast';
import { usePublicTheme } from '../hooks/usePublicTheme';
import '../styles/publicTheme.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COACH_TYPES = { 
  head_coach: 'مدرب رئيسي', 
  assistant_coach: 'مدرب مساعد', 
  goalkeeper_coach: 'مدرب حراس مرمى', 
  fitness_coach: 'مدرب لياقة بدنية', 
  conditioning_coach: 'مدرب إعداد بدني', 
  performance_analyst: 'محلل أداء فني', 
  youth_coach: 'مدرب فئات سنية', 
  other: 'تخصص رياضي آخر' 
};

const AVAILABILITY_LABELS = { 
  available: { label: 'متاح للتعاقد', bg: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30' }, 
  available_offers_only: { label: 'متاح للعروض فقط', bg: 'bg-amber-500/10 text-amber-400 border-amber-500/30' }, 
  unavailable: { label: 'غير متاح حالياً', bg: 'bg-slate-800 text-slate-400 border-slate-700' } 
};

// بطاقة المدرب المحسّنة
const CoachCard = ({ c, onOpen }) => {
  const photoUrl = c.photo_file_id ? `${API}/files/${c.photo_file_id}` : null;
  const avail = AVAILABILITY_LABELS[c.availability_status] || AVAILABILITY_LABELS.available;

  return (
    <div 
      onClick={() => onOpen(c.id)} 
      className="group relative flex flex-col justify-between rounded-2xl border border-teal-900/40 bg-[#0d1f38]/80 p-6 text-right transition-all duration-300 hover:-translate-y-1.5 hover:border-teal-500/50 hover:bg-[#0f2545] hover:shadow-2xl hover:shadow-teal-950/50 cursor-pointer overflow-hidden backdrop-blur-md"
      data-testid="coach-directory-card"
    >
      {/* Top Accent Line on Hover */}
      <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-teal-500 via-cyan-400 to-orange-500 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />

      <div>
        {/* Header section with larger photo & badge */}
        <div className="flex items-start gap-4">
          <div className="relative shrink-0">
            {photoUrl ? (
              <img 
                src={photoUrl} 
                alt={c.full_name_ar} 
                className="h-20 w-20 rounded-2xl object-cover ring-2 ring-teal-500/30 group-hover:ring-teal-400/80 transition-all duration-300 shadow-md" 
              />
            ) : (
              <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-gradient-to-br from-slate-800 to-teal-950 text-2xl font-black text-teal-300 ring-2 ring-teal-500/30 group-hover:ring-teal-400/80 transition-all duration-300">
                {(c.full_name_ar || '؟').slice(0, 1)}
              </div>
            )}
            {c.has_verified_certification && (
              <div className="absolute -bottom-1.5 -left-1.5 flex h-7 w-7 items-center justify-center rounded-full bg-teal-500 text-slate-950 shadow-lg ring-2 ring-[#0d1f38]" title="شهادة موثقة">
                <ShieldCheck className="h-4 w-4 stroke-[2.5]" />
              </div>
            )}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-lg font-bold text-white group-hover:text-teal-300 transition-colors truncate">
                {c.full_name_ar}
              </h3>
            </div>
            
            <p className="mt-1 text-xs font-medium text-teal-400/90">
              {COACH_TYPES[c.coach_type] || c.coach_type}
            </p>

            {c.has_verified_certification && (
              <div className="mt-2 inline-flex items-center gap-1 rounded-md bg-teal-500/10 px-2 py-0.5 text-[11px] font-semibold text-teal-300 border border-teal-500/20">
                <Award className="h-3 w-3 text-teal-400" />
                <span>مُعتمد وموثّق</span>
              </div>
            )}
          </div>
        </div>

        {/* Bio preview if available */}
        {c.bio && (
          <p className="mt-4 text-xs text-slate-300/80 line-clamp-2 leading-relaxed">
            {c.bio}
          </p>
        )}

        {/* Meta details */}
        <div className="mt-5 grid grid-cols-2 gap-2 text-xs text-slate-300">
          <div className="flex items-center gap-1.5 rounded-lg bg-[#071326]/60 px-3 py-2 border border-slate-800/80">
            <MapPin className="h-3.5 w-3.5 text-orange-400 shrink-0" />
            <span className="truncate">{c.city || 'غير مخصص'}</span>
          </div>

          <div className="flex items-center gap-1.5 rounded-lg bg-[#071326]/60 px-3 py-2 border border-slate-800/80">
            <Briefcase className="h-3.5 w-3.5 text-teal-400 shrink-0" />
            <span>{c.years_experience ? `${c.years_experience} سنوات خبرة` : 'جديد'}</span>
          </div>
        </div>
      </div>

      {/* Footer / CTA */}
      <div className="mt-5 pt-4 border-t border-slate-800/80 flex items-center justify-between gap-3">
        <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-semibold border ${avail.bg}`}>
          <span className="h-1.5 w-1.5 rounded-full bg-current animate-pulse" />
          {avail.label}
        </span>

        <span className="inline-flex items-center gap-1 text-xs font-bold text-teal-400 group-hover:text-teal-300 group-hover:translate-x-[-2px] transition-all">
          <span>استعراض الملف</span>
          <ChevronLeft className="h-4 w-4" />
        </span>
      </div>
    </div>
  );
};

// نافذة الملف التفصيلي + نموذج طلب التواصل
const ProfileModal = ({ coachId, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showContactForm, setShowContactForm] = useState(false);
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [contactSuccess, setContactSubmittingSuccess] = useState(false);
  
  const [form, setForm] = useState({
    sender_name: '',
    contact_info: '',
    organization_name: '',
    message: ''
  });

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/coaches/directory/${coachId}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [coachId]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!form.sender_name.trim() || !form.contact_info.trim() || !form.message.trim()) {
      toast.error('يرجى تعبئة كافة الحقول المطلوبة (الاسم، وسيلة التواصل، والرسالة)');
      return;
    }

    setContactSubmitting(true);
    try {
      await axios.post(`${API}/coaches/directory/${coachId}/public-contact`, form);
      setContactSubmittingSuccess(true);
      toast.success('تم إرسال طلب التواصل للمدرب بنجاح!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'تعذر إرسال طلب التواصل، يرجى المحاولة لاحقاً');
    } finally {
      setContactSubmitting(false);
    }
  };

  const photoUrl = data?.photo_file_id ? `${API}/files/${data.photo_file_id}` : null;
  const avail = data ? (AVAILABILITY_LABELS[data.availability_status] || AVAILABILITY_LABELS.available) : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 backdrop-blur-md p-0 sm:p-6" dir="rtl" data-testid="coach-profile-modal">
      <div className="relative flex h-full w-full flex-col overflow-hidden rounded-none border-0 bg-[#0A192F] text-right text-slate-100 shadow-2xl sm:h-auto sm:max-h-[90vh] sm:w-full sm:max-w-3xl sm:rounded-3xl sm:border sm:border-teal-900/50">
        
        {/* Modal Top Header Banner */}
        <div className="relative h-28 sm:h-32 shrink-0 bg-gradient-to-r from-teal-950 via-[#0d2847] to-slate-900 px-6 py-4 flex items-start justify-between">
          <div className="absolute inset-0 bg-[radial-gradient(#0d9488_1px,transparent_1px)] [background-size:16px_16px] opacity-10" />
          <button 
            onClick={onClose} 
            className="relative z-10 flex h-9 w-9 items-center justify-center rounded-full bg-slate-900/60 text-slate-300 hover:bg-slate-800 hover:text-white transition-colors" 
            data-testid="coach-profile-modal-close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Modal Content — scrollable, close button and CTA stay fixed outside this area */}
        <div className="relative flex-1 overflow-y-auto overscroll-contain px-6 pb-6 pt-0 -mt-16">
          {loading ? (
            <div className="py-20 text-center text-slate-400">
              <RefreshCw className="mx-auto h-8 w-8 animate-spin text-teal-400" />
              <p className="mt-3 text-sm font-medium">جاري تحميل بيانات الملف المهني للمدرب...</p>
            </div>
          ) : !data ? (
            <div className="py-16 text-center text-slate-400">
              <p>الملف غير متاح حالياً أو تعذر جلب البيانات.</p>
            </div>
          ) : (
            <div>
              {/* Profile Main Header */}
              <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-4 pb-6 border-b border-slate-800">
                <div className="flex items-end gap-4">
                  <div className="relative">
                    {photoUrl ? (
                      <img src={photoUrl} alt="" className="h-24 w-20 sm:h-28 sm:w-28 rounded-2xl object-cover ring-4 ring-[#0A192F] shadow-xl" />
                    ) : (
                      <div className="flex h-24 w-24 sm:h-28 sm:w-28 items-center justify-center rounded-2xl bg-gradient-to-br from-slate-800 to-teal-950 text-3xl font-black text-teal-300 ring-4 ring-[#0A192F] shadow-xl">
                        {(data.full_name_ar || '؟').slice(0, 1)}
                      </div>
                    )}
                  </div>

                  <div>
                    <h2 className="text-2xl font-black text-white flex items-center gap-2">
                      {data.full_name_ar}
                      {data.has_verified_certification && (
                        <ShieldCheck className="h-6 w-6 text-teal-400" title="شهادة موثقة" />
                      )}
                    </h2>
                    <p className="text-sm font-bold text-teal-400 mt-0.5">
                      {COACH_TYPES[data.coach_type] || data.coach_type}
                    </p>
                    <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-slate-300">
                      {data.city && (
                        <span className="flex items-center gap-1">
                          <MapPin className="h-3.5 w-3.5 text-orange-400" /> {data.city}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Briefcase className="h-3.5 w-3.5 text-teal-400" /> {data.years_experience ?? 0} سنوات خبرة
                      </span>
                      {avail && (
                        <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-semibold border ${avail.bg}`}>
                          {avail.label}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Direct Public Contact Form */}
              {showContactForm && (
                <div className="mt-6 rounded-2xl border border-teal-500/30 bg-[#0d2242] p-6 shadow-xl" data-testid="coach-contact-form">
                  <div className="flex items-center gap-2 text-teal-300 font-bold text-base mb-2">
                    <Send className="h-5 w-5 text-orange-400" />
                    <h3>إرسال طلب تواصل مباشر مع المدرب ({data.full_name_ar})</h3>
                  </div>
                  <p className="text-xs text-slate-300 mb-5">
                    يمكنك إرسال رسالة مباشرة للمدرب دون الحاجة لتسجيل دخول منشأة. سيصل إشعار فوري للمدرب ببياناتك للرد عليك.
                  </p>

                  {contactSuccess ? (
                    <div className="rounded-xl bg-emerald-500/10 border border-emerald-500/30 p-5 text-center text-emerald-300">
                      <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-400 mb-2" />
                      <h4 className="font-bold text-base">تم إرسال طلب التواصل بنجاح!</h4>
                      <p className="mt-1 text-xs text-emerald-200/80">
                        شكرًا لك. تم تحويل الرسالة إلى المدرب وسيقوم بالتواصل معك عبر البريد/الجوال المرفق.
                      </p>
                      <button 
                        onClick={() => { setContactSubmittingSuccess(false); setShowContactForm(false); }}
                        className="mt-4 rounded-lg bg-emerald-500 px-4 py-1.5 text-xs font-bold text-slate-950 hover:bg-emerald-400"
                      >
                        إغلاق
                      </button>
                    </div>
                  ) : (
                    <form onSubmit={handleContactSubmit} className="space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-semibold text-slate-300 mb-1">
                            الاسم الكامل <span className="text-orange-400">*</span>
                          </label>
                          <div className="relative">
                            <User className="absolute right-3 top-2.5 h-4 w-4 text-slate-400" />
                            <input 
                              type="text" 
                              required 
                              placeholder="اسمك أو اسم المسؤول" 
                              className="w-full rounded-xl border border-slate-700 bg-[#071326] py-2 pr-9 pl-3 text-xs text-white placeholder-slate-500 focus:border-teal-500 focus:outline-none" 
                              value={form.sender_name} 
                              onChange={(e) => setForm({ ...form, sender_name: e.target.value })} 
                              data-testid="coach-contact-name-input"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-xs font-semibold text-slate-300 mb-1">
                            رقم الجوال أو البريد <span className="text-orange-400">*</span>
                          </label>
                          <div className="relative">
                            <PhoneCall className="absolute right-3 top-2.5 h-4 w-4 text-slate-400" />
                            <input 
                              type="text" 
                              required 
                              placeholder="05xxxxxxx أو email@domain.com" 
                              className="w-full rounded-xl border border-slate-700 bg-[#071326] py-2 pr-9 pl-3 text-xs text-white placeholder-slate-500 focus:border-teal-500 focus:outline-none" 
                              value={form.contact_info} 
                              onChange={(e) => setForm({ ...form, contact_info: e.target.value })} 
                              data-testid="coach-contact-info-input"
                            />
                          </div>
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-slate-300 mb-1">
                          اسم النادي / الأكاديمية / المنشأة (اختياري)
                        </label>
                        <div className="relative">
                          <Building className="absolute right-3 top-2.5 h-4 w-4 text-slate-400" />
                          <input 
                            type="text" 
                            placeholder="مثال: نادي الفتح / أكاديمية الأبطال" 
                            className="w-full rounded-xl border border-slate-700 bg-[#071326] py-2 pr-9 pl-3 text-xs text-white placeholder-slate-500 focus:border-teal-500 focus:outline-none" 
                            value={form.organization_name} 
                            onChange={(e) => setForm({ ...form, organization_name: e.target.value })} 
                            data-testid="coach-contact-org-input"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-slate-300 mb-1">
                          تفاصيل الرسالة أو العرض <span className="text-orange-400">*</span>
                        </label>
                        <textarea 
                          rows={3} 
                          required 
                          placeholder="اكتب رسالتك للمدرب، مثل تفاصيل الفرصة التدريبية المتاحة لديك..." 
                          className="w-full rounded-xl border border-slate-700 bg-[#071326] p-3 text-xs text-white placeholder-slate-500 focus:border-teal-500 focus:outline-none" 
                          value={form.message} 
                          onChange={(e) => setForm({ ...form, message: e.target.value })} 
                          data-testid="coach-contact-message-input"
                        />
                      </div>

                      <div className="flex justify-end gap-2 pt-2">
                        <button 
                          type="button" 
                          onClick={() => setShowContactForm(false)} 
                          className="rounded-xl border border-slate-700 px-4 py-2 text-xs font-semibold text-slate-300 hover:bg-slate-800"
                        >
                          إلغاء
                        </button>
                        <button 
                          type="submit" 
                          disabled={contactSubmitting} 
                          className="flex items-center gap-2 rounded-xl bg-gradient-to-r from-orange-500 to-amber-500 px-5 py-2 text-xs font-bold text-slate-950 hover:from-orange-400 hover:to-amber-400 disabled:opacity-50" 
                          data-testid="coach-contact-submit-btn"
                        >
                          {contactSubmitting ? (
                            <>
                              <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                              <span>جاري الإرسال...</span>
                            </>
                          ) : (
                            <>
                              <Send className="h-3.5 w-3.5" />
                              <span>إرسال طلب التواصل</span>
                            </>
                          )}
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              )}

              {/* Bio Section */}
              {data.bio && (
                <div className="mt-6">
                  <h3 className="text-sm font-bold text-slate-200 flex items-center gap-1.5 mb-2">
                    <User className="h-4 w-4 text-teal-400" />
                    <span>نبذة عن المدرب</span>
                  </h3>
                  <p className="text-xs text-slate-300 leading-relaxed bg-[#071326]/60 p-4 rounded-xl border border-slate-800/80">
                    {data.bio}
                  </p>
                </div>
              )}

              {/* Badges / Languages / Categories */}
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {data.age_categories?.length > 0 && (
                  <div>
                    <h4 className="text-xs font-bold text-slate-400 mb-2 flex items-center gap-1">
                      <GraduationCap className="h-3.5 w-3.5 text-teal-400" />
                      <span>الفئات السنية المفضلة</span>
                    </h4>
                    <div className="flex flex-wrap gap-1.5">
                      {data.age_categories.map((a) => (
                        <span key={a} className="rounded-lg bg-teal-500/10 border border-teal-500/20 px-2.5 py-1 text-xs font-medium text-teal-300">
                          {a}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {data.languages?.length > 0 && (
                  <div>
                    <h4 className="text-xs font-bold text-slate-400 mb-2 flex items-center gap-1">
                      <Globe className="h-3.5 w-3.5 text-orange-400" />
                      <span>اللغات</span>
                    </h4>
                    <div className="flex flex-wrap gap-1.5">
                      {data.languages.map((l) => (
                        <span key={l} className="rounded-lg bg-slate-800 border border-slate-700 px-2.5 py-1 text-xs font-medium text-slate-300">
                          {l}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Certifications list */}
              {data.certifications?.length > 0 && (
                <div className="mt-6">
                  <h3 className="text-sm font-bold text-slate-200 flex items-center gap-1.5 mb-3">
                    <Award className="h-4 w-4 text-teal-400" />
                    <span>الشهادات والرخص المعتمدة</span>
                  </h3>
                  <div className="space-y-2">
                    {data.certifications.map((c) => (
                      <div key={c.id} className="flex items-center justify-between rounded-xl bg-[#071326]/60 p-3 border border-slate-800 text-xs">
                        <div className="flex items-center gap-2">
                          <Award className="h-4 w-4 text-orange-400" />
                          <div>
                            <span className="font-bold text-white">{c.name}</span>
                            <span className="text-slate-400 mx-2">—</span>
                            <span className="text-slate-400">{c.issuing_authority}</span>
                          </div>
                        </div>
                        {c.verification_status === 'verified' && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-teal-500/10 border border-teal-500/30 px-2.5 py-0.5 text-[11px] font-bold text-teal-400">
                            <ShieldCheck className="h-3 w-3" /> موثقة
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Experiences list */}
              {data.experiences?.length > 0 && (
                <div className="mt-6">
                  <h3 className="text-sm font-bold text-slate-200 flex items-center gap-1.5 mb-3">
                    <Clock className="h-4 w-4 text-orange-400" />
                    <span>الخبرات التدريبية السابقة</span>
                  </h3>
                  <div className="space-y-2">
                    {data.experiences.map((e) => (
                      <div key={e.id} className="rounded-xl bg-[#071326]/60 p-3.5 border border-slate-800 text-xs">
                        <div className="flex justify-between items-center">
                          <span className="font-bold text-white text-sm">{e.title}</span>
                          <span className="text-slate-400 text-[11px]">{e.organization_name}</span>
                        </div>
                        {e.description && (
                          <p className="mt-1.5 text-slate-300 text-[11px] leading-relaxed">{e.description}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Sticky footer CTA — always reachable regardless of scroll position */}
        {!loading && data && (
          <div className="shrink-0 border-t border-slate-800 bg-[#0A192F] px-6 py-3">
            <button
              onClick={() => setShowContactForm((v) => !v)}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-teal-500 to-teal-600 px-5 py-3 text-sm font-bold text-slate-950 hover:from-teal-400 hover:to-teal-500 transition-all shadow-lg shadow-teal-950/50"
              data-testid="coach-contact-btn"
            >
              <MessageSquare className="h-4 w-4" />
              <span>{showContactForm ? 'إخفاء نموذج التواصل' : 'طلب تواصل مباشر'}</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const pluralizeAvailableCoaches = (n) => {
  if (n === 0) return 'لا يوجد مدربون متاحون حالياً للتعاقد والاستعراض';
  if (n === 1) return 'مدرب واحد متاح حالياً للتعاقد والاستعراض';
  if (n === 2) return 'مدربان متاحان حالياً للتعاقد والاستعراض';
  if (n >= 3 && n <= 10) return `${n} مدربين متاحين حالياً للتعاقد والاستعراض`;
  return `${n} مدرباً متاحاً حالياً للتعاقد والاستعراض`;
};

export default function CoachDirectoryPage() {
  const { theme, toggleTheme } = usePublicTheme();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [registeredCoaches, setRegisteredCoaches] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [openCoach, setOpenCoach] = useState(null);

  // Dynamic filter options from server
  const [cityOptions, setCityOptions] = useState([]);
  const [certOptions, setCertOptions] = useState([]);

  const [filters, setFilters] = useState({ 
    search: '', 
    city: '', 
    certification: '',
    specialty: '', 
    availability: '',
    game: '' 
  });

  const pageSize = 12;

  // Load registered cities and certifications options
  useEffect(() => {
    axios.get(`${API}/coaches/directory/options`)
      .then((res) => {
        if (res.data) {
          setCityOptions(res.data.cities || []);
          setCertOptions(res.data.certifications || []);
        }
      })
      .catch(() => {
        setCityOptions([]);
        setCertOptions([]);
      });
    axios.get(`${API}/saas/public-stats`)
      .then((res) => setRegisteredCoaches(res.data?.active_coaches ?? null))
      .catch(() => setRegisteredCoaches(null));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await axios.get(`${API}/coaches/directory`, { params });
      setItems(res.data.items || []); 
      setTotal(res.data.total || 0);
    } catch { 
      setItems([]); 
      setTotal(0); 
    }
    setLoading(false);
  }, [page, filters]);

  useEffect(() => { 
    load(); 
  }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const resetFilters = () => {
    setFilters({ search: '', city: '', certification: '', specialty: '', availability: '', game: '' });
    setPage(1);
  };

  const hasActiveFilters = Object.values(filters).some(Boolean);

  return (
    <div className="min-h-screen bg-[#060f1c] text-slate-100 font-sans dir-rtl" dir="rtl" data-testid="coach-directory-page" data-pub-theme={theme}>
      {/* Dark Styled Navigation Header */}
      <PublicCoachHeader title="دليل المدربين العام" dark={true} theme={theme} onToggleTheme={toggleTheme} />

      {/* Hero Banner Section */}
      <div className="relative pt-24 pb-12 overflow-hidden border-b border-teal-900/30 bg-gradient-to-b from-[#0A192F] via-[#060f1c] to-[#060f1c]">
        <div className="absolute inset-0 bg-[radial-gradient(#0d9488_1px,transparent_1px)] [background-size:24px_24px] opacity-15" />
        <div className="relative mx-auto max-w-7xl px-5 text-right">
          
          <div className="inline-flex items-center gap-2 rounded-full border border-teal-500/30 bg-teal-500/10 px-4 py-1.5 text-xs font-bold text-teal-300 mb-4 backdrop-blur-md">
            <Sparkles className="h-4 w-4 text-orange-400" />
            <span>منصة استقطاب الكفاءات الرياضية والمدربين</span>
          </div>

          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight leading-tight">
            دليل <span className="bg-gradient-to-r from-teal-400 via-cyan-300 to-orange-400 bg-clip-text text-transparent">المدربين المحترفين</span>
          </h1>
          
          <p className="mt-3 text-sm sm:text-base text-slate-300 max-w-2xl leading-relaxed">
            استكشف قاعدة بيانات تضم أفضل الكوادر والمدربين الرياضيين في المملكة، واستعرض ملفاتهم المهنية وتواصل معهم مباشرة.
          </p>

          <div className="mt-4 flex flex-col gap-1.5 text-xs font-semibold text-teal-400 sm:flex-row sm:items-center sm:gap-4" data-testid="coach-directory-stats">
            <span className="flex items-center gap-2">
              <span className="flex h-2 w-2 rounded-full bg-emerald-400 animate-ping" />
              <span data-testid="coach-directory-available-count">{pluralizeAvailableCoaches(total)}</span>
            </span>
            {registeredCoaches !== null && registeredCoaches > total && (
              <span className="text-slate-400" data-testid="coach-directory-registered-total">
                (من إجمالي {registeredCoaches}+ مدرب مسجّل في تسيار — الدليل يعرض فقط الملفات المهنية العامة المفعّلة)
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Main Container */}
      <div className="mx-auto max-w-7xl px-5 py-8">
        
        {/* Filters Section */}
        <div className="rounded-2xl border border-teal-900/40 bg-[#0A192F]/90 p-5 shadow-2xl backdrop-blur-xl">
          <div className="flex items-center justify-between gap-2 mb-4 pb-3 border-b border-slate-800">
            <div className="flex items-center gap-2 text-sm font-bold text-teal-300">
              <Filter className="h-4 w-4 text-orange-400" />
              <span>فلاتر البحث المتقدمة</span>
            </div>

            {hasActiveFilters && (
              <button 
                onClick={resetFilters} 
                className="flex items-center gap-1.5 text-xs font-semibold text-slate-400 hover:text-orange-400 transition-colors"
              >
                <RefreshCw className="h-3.5 w-3.5" />
                <span>إعادة ضبط الفلاتر</span>
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
            {/* Search Input */}
            <div className="relative">
              <Search className="absolute right-3.5 top-3 h-4 w-4 text-slate-400" />
              <input 
                placeholder="بحث باسم المدرب..." 
                className="w-full rounded-xl border border-slate-800 bg-[#071326] py-2.5 pr-10 pl-3 text-xs text-white placeholder-slate-500 focus:border-teal-500 focus:outline-none transition-colors" 
                value={filters.search} 
                onChange={(e) => { setPage(1); setFilters({ ...filters, search: e.target.value }); }} 
                data-testid="coach-directory-search-input" 
              />
            </div>

            {/* City Dropdown (Real Server Data) */}
            <div>
              <select 
                className="w-full rounded-xl border border-slate-800 bg-[#071326] px-3 py-2.5 text-xs text-white focus:border-teal-500 focus:outline-none transition-colors" 
                value={filters.city} 
                onChange={(e) => { setPage(1); setFilters({ ...filters, city: e.target.value }); }} 
                data-testid="coach-directory-city-select"
              >
                <option value="">كل المدن</option>
                {cityOptions.map((city) => (
                  <option key={city} value={city}>{city}</option>
                ))}
              </select>
            </div>

            {/* Certification Dropdown (Real Server Data) */}
            <div>
              <select 
                className="w-full rounded-xl border border-slate-800 bg-[#071326] px-3 py-2.5 text-xs text-white focus:border-teal-500 focus:outline-none transition-colors" 
                value={filters.certification} 
                onChange={(e) => { setPage(1); setFilters({ ...filters, certification: e.target.value }); }} 
                data-testid="coach-directory-cert-select"
              >
                <option value="">كل الشهادات المسجلة</option>
                {certOptions.map((cert) => (
                  <option key={cert} value={cert}>{cert}</option>
                ))}
              </select>
            </div>

            {/* Specialty Dropdown */}
            <div>
              <select 
                className="w-full rounded-xl border border-slate-800 bg-[#071326] px-3 py-2.5 text-xs text-white focus:border-teal-500 focus:outline-none transition-colors" 
                value={filters.specialty} 
                onChange={(e) => { setPage(1); setFilters({ ...filters, specialty: e.target.value }); }} 
                data-testid="coach-directory-specialty-select"
              >
                <option value="">كل التخصصات التدريبية</option>
                {Object.entries(COACH_TYPES).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>

            {/* Availability Dropdown */}
            <div>
              <select 
                className="w-full rounded-xl border border-slate-800 bg-[#071326] px-3 py-2.5 text-xs text-white focus:border-teal-500 focus:outline-none transition-colors" 
                value={filters.availability} 
                onChange={(e) => { setPage(1); setFilters({ ...filters, availability: e.target.value }); }} 
                data-testid="coach-directory-availability-select"
              >
                <option value="">كل حالات التوفر</option>
                {Object.entries(AVAILABILITY_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Coach Cards Grid Section */}
        <div className="mt-8">
          {loading ? (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="h-64 animate-pulse rounded-2xl bg-[#0d1f38]/60 border border-slate-800/80" />
              ))}
            </div>
          ) : items.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-800 bg-[#0A192F]/40 py-20 text-center" data-testid="coach-directory-empty">
              <User className="mx-auto h-12 w-12 text-slate-600 mb-3" />
              <h3 className="text-base font-bold text-slate-300">لا توجد نتائج مطابقة لبحثك</h3>
              <p className="mt-1 text-xs text-slate-500">جرب تغيير فلاتر البحث أو كلمة البحث للوصول لنتائج أفضل.</p>
              {hasActiveFilters && (
                <button 
                  onClick={resetFilters} 
                  className="mt-4 rounded-xl bg-teal-500/10 border border-teal-500/30 px-4 py-2 text-xs font-bold text-teal-300 hover:bg-teal-500/20"
                >
                  إعادة ضبط جميع الفلاتر
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {items.map((c) => (
                <CoachCard key={c.id} c={c} onOpen={setOpenCoach} />
              ))}
            </div>
          )}
        </div>

        {/* Pagination Section */}
        {totalPages > 1 && (
          <div className="mt-12 flex items-center justify-center gap-3 text-xs" data-testid="coach-directory-pagination">
            <button 
              disabled={page <= 1} 
              onClick={() => { setPage(page - 1); window.scrollTo({ top: 0, behavior: 'smooth' }); }} 
              className="flex items-center gap-1 rounded-xl border border-slate-800 bg-[#0A192F] px-4 py-2 font-bold text-slate-300 hover:border-teal-500/50 hover:text-white disabled:opacity-40 disabled:cursor-not-allowed transition-all"
            >
              <ChevronRight className="h-4 w-4" />
              <span>الصفحة السابقة</span>
            </button>

            <span className="rounded-xl border border-slate-800 bg-[#071326] px-4 py-2 font-bold text-teal-400">
              {page} من {totalPages}
            </span>

            <button 
              disabled={page >= totalPages} 
              onClick={() => { setPage(page + 1); window.scrollTo({ top: 0, behavior: 'smooth' }); }} 
              className="flex items-center gap-1 rounded-xl border border-slate-800 bg-[#0A192F] px-4 py-2 font-bold text-slate-300 hover:border-teal-500/50 hover:text-white disabled:opacity-40 disabled:cursor-not-allowed transition-all"
            >
              <span>الصفحة التالية</span>
              <ChevronLeft className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>

      {/* Modal Profile view */}
      {openCoach && (
        <ProfileModal coachId={openCoach} onClose={() => setOpenCoach(null)} />
      )}
    </div>
  );
}
